import React, { FC, useCallback } from "react";
import {
  DateRange,
  LocalizationProvider,
  PickersActionBarProps,
  SingleInputDateRangeFieldProps,
} from "@mui/x-date-pickers-pro";
import dayjs, { Dayjs } from "dayjs";
import {
  DateRangePicker,
  DateRangePickerProps,
} from "@mui/x-date-pickers-pro/DateRangePicker";

import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";

import { Button, DialogActions, useForkRef, Tooltip } from "@mui/material";
import DateRangeIcon from "@mui/icons-material/DateRange";
import { useTranslation } from "react-i18next";
import {
  localeSelected,
  manageFormat,
} from "../../../../../dateRangePickersHelpers/DateRangePickersHelpers";
import { useSelector } from "react-redux";

import { localeData } from "../../../../../../redux/slices/dataTable/dataTableFilters";
import { DateRangePickerStyles } from "./rangePickerStyles";

import { ColumnsForDataGridPro } from "../../../../../../redux/actions/dataTable/DataTableTypes";

interface DateRangeButtonFieldProps
  extends SingleInputDateRangeFieldProps<Dayjs> {
  filtersSelected: number;
  openRangeDate?: any;
  hasNoData?: boolean;
  loading?: boolean;
}

type DateRangeButtonFieldComponent = ((
  props: DateRangeButtonFieldProps & React.RefAttributes<HTMLDivElement>
) => React.JSX.Element) & { fieldType?: string };

const DateRangeButtonField = React.forwardRef(
  (props: DateRangeButtonFieldProps, ref: React.Ref<HTMLDivElement>) => {
    const {
      openRangeDate,
      label,
      id,
      filtersSelected,
      disabled,
      hasNoData = false,
      loading = false,
      InputProps: { ref: containerRef } = {},
      inputProps: { "aria-label": ariaLabel } = {},
    } = props;

    const [t] = useTranslation("global");
    const handleRef = useForkRef(ref, containerRef);

    const button = (
      <Button
        sx={{
          ...DateRangePickerStyles.DateButton,
          ...(hasNoData &&
            filtersSelected === 0 &&
            !loading &&
            DateRangePickerStyles.dateButtonOrange),
        }}
        endIcon={<DateRangeIcon />}
        id={id}
        disabled={disabled}
        ref={handleRef}
        aria-label={ariaLabel}
        onClick={() => openRangeDate()}
      >
        {label}
      </Button>
    );

    if (hasNoData && filtersSelected === 0 && !loading) {
      return (
        <Tooltip title={t("Data.no_results_data")} arrow>
          {button}
        </Tooltip>
      );
    }

    return button;
  }
) as DateRangeButtonFieldComponent;

DateRangeButtonField.fieldType = "single-input";

const MyActionBar = React.memo(
  ({
    onAccept,
    onCancel,
    setActivityDate,
    callTheFetchData,
  }: PickersActionBarProps & {
    setActivityDate: (date: Date[]) => void;
    callTheFetchData: () => void;
  }) => {
    const [t] = useTranslation("global");

    const handlePresetMonths = useCallback(
      (months: number) => {
        const endDate = new Date();
        endDate.setHours(23, 59, 59, 999);

        const startDate = new Date();
        startDate.setMonth(endDate.getMonth() - months);
        startDate.setHours(23, 59, 59, 999);

        setActivityDate([startDate, endDate]);
        requestAnimationFrame(() => {
          callTheFetchData();
        });
      },
      [setActivityDate, callTheFetchData]
    );

    const handlePresetDays = useCallback(
      (days: number) => {
        const endDate = new Date();
        endDate.setHours(23, 59, 59, 999);

        const startDate = new Date();
        startDate.setDate(endDate.getDate() - days);
        startDate.setHours(23, 59, 59, 999);

        setActivityDate([startDate, endDate]);
        requestAnimationFrame(() => {
          callTheFetchData();
        });
      },
      [setActivityDate, callTheFetchData]
    );

    return (
      <DialogActions
        sx={{
          ...DateRangePickerStyles.dialogActions,
          ...DateRangePickerStyles.dialogActionsExpanded,
        }}
      >
        <div style={DateRangePickerStyles.containerDiv}>
          <div style={DateRangePickerStyles.presetsContainer}>
            <Button
              variant="outlined"
              size="small"
              sx={{
                ...DateRangePickerStyles.cancelApplyButton,
                ...DateRangePickerStyles.presetButton,
              }}
              onClick={() => handlePresetDays(7)}
            >
              {t("Range_picker.last_7_days")}
            </Button>
            <Button
              variant="outlined"
              size="small"
              sx={{
                ...DateRangePickerStyles.cancelApplyButton,
                ...DateRangePickerStyles.presetButton,
              }}
              onClick={() => handlePresetDays(14)}
            >
              {t("Range_picker.last_14_days")}
            </Button>
            <Button
              variant="outlined"
              size="small"
              sx={{
                ...DateRangePickerStyles.cancelApplyButton,
                ...DateRangePickerStyles.presetButton,
              }}
              onClick={() => handlePresetDays(30)}
            >
              {t("Range_picker.last_30_days")}
            </Button>
            <Button
              variant="outlined"
              size="small"
              sx={{
                ...DateRangePickerStyles.cancelApplyButton,
                ...DateRangePickerStyles.presetButton,
              }}
              onClick={() => handlePresetMonths(2)}
            >
              {t("Range_picker.last_2_months")}
            </Button>
          </div>

          <div style={DateRangePickerStyles.actionsContainer}>
            <Button
              variant="outlined"
              sx={DateRangePickerStyles.cancelApplyButton}
              onClick={onCancel}
            >
              {t("Common_options.Cancel")}
            </Button>
            <Button
              variant="contained"
              sx={DateRangePickerStyles.cancelApplyButton}
              onClick={onAccept}
            >
              {t("Common_options.Apply")}
            </Button>
          </div>
        </div>
      </DialogActions>
    );
  }
);

const ButtonDateRangePicker = React.forwardRef(
  (
    props: Omit<DateRangePickerProps<Dayjs>, "open" | "onOpen" | "onClose"> & {
      hasNoData?: boolean;
      loading?: boolean;
      filtersSelected: number;
      openRangeDate: () => void;
      closeRangeDate: () => void;
      setActivityDate: (date: Date[]) => void;
      callTheFetchData: () => void;
      selectDataDateActivity: Date[];
      open: boolean;
    },
    ref: React.Ref<HTMLDivElement>
  ) => {
    const loadings = props.loading || false;

    const { openRangeDate, closeRangeDate, setActivityDate, callTheFetchData } =
      props;
    const [t] = useTranslation("global");

    // Create a wrapper component for MyActionBar with injected props
    const ActionBarWrapper = React.useCallback(
      (actionBarProps: PickersActionBarProps) => (
        <MyActionBar
          {...actionBarProps}
          setActivityDate={setActivityDate}
          callTheFetchData={callTheFetchData}
        />
      ),
      [setActivityDate, callTheFetchData]
    );

    return (
      <DateRangePicker
        slots={{
          field: DateRangeButtonField,
          ...props.slots,
          actionBar: ActionBarWrapper,
        }}
        disabled={loadings}
        disableFuture={true}
        localeText={{
          cancelButtonLabel: t("Common_options.Apply"),
          okButtonLabel: t("Common_options.Cancel"),
        }}
        value={[
          dayjs(props.selectDataDateActivity[0]),
          dayjs(props.selectDataDateActivity[1]),
        ]}
        slotProps={{
          field: {
            openRangeDate,
            hasNoData: props.hasNoData,
            loading: props.loading,
            filtersSelected: props.filtersSelected,
          } as any,
          actionBar: {
            actions: ["cancel", "accept"],
          },
        }}
        onChange={(newValue: DateRange<unknown>) => {
          setActivityDate(newValue as Date[]);
        }}
        onAccept={() => {
          callTheFetchData();
        }}
        closeOnSelect={false}
        ref={ref}
        {...props}
        open={props.open}
        onClose={() => closeRangeDate()}
        onOpen={() => openRangeDate()}
      />
    );
  }
);

type DataDateRangePickerProps = {
  setActivityDate: (date: Date[]) => void;
  selectDataDateActivity: Date[];
  openRangeDate: () => void;
  closeRangeDate: () => void;
  callTheFetchData: () => void;
  open: boolean;
  //optional
  filtersSelectedLength?: number;
  loading?: boolean;
  actionsDataTable?: any[];
  columnsDataTable?: ColumnsForDataGridPro[];
  tableOptions?: any;
  noData?: boolean;
  hasDataBeenLoaded2?: boolean;
};

const DataDateRangePicker: FC<DataDateRangePickerProps> = ({
  setActivityDate,
  selectDataDateActivity,
  openRangeDate,
  closeRangeDate,
  callTheFetchData,
  open,
  //optional
  filtersSelectedLength,
  loading,
  actionsDataTable,
  columnsDataTable,
  tableOptions,
  hasDataBeenLoaded2,
  noData,
}) => {
  const locale = useSelector(localeData);
  const hasDataBeenLoaded = tableOptions?.totalRows !== null;
  const hasNoData = actionsDataTable?.length === 0 && hasDataBeenLoaded;

  const hasNoData2 = noData && hasDataBeenLoaded2;

  return (
    <LocalizationProvider
      adapterLocale={localeSelected(locale) as unknown as string}
      dateAdapter={AdapterDayjs}
    >
      <ButtonDateRangePicker
        open={open}
        selectDataDateActivity={selectDataDateActivity}
        filtersSelected={filtersSelectedLength ?? 0}
        disabled={columnsDataTable?.length === 0 || loading}
        hasNoData={hasNoData || hasNoData2}
        loading={loading}
        openRangeDate={openRangeDate}
        closeRangeDate={closeRangeDate}
        setActivityDate={setActivityDate}
        callTheFetchData={callTheFetchData}
        label={`${dayjs(selectDataDateActivity[0]).format(
          manageFormat(locale)
        )} - ${dayjs(selectDataDateActivity[1]).format(manageFormat(locale))}`}
        value={[
          dayjs(selectDataDateActivity[0]),
          dayjs(selectDataDateActivity[1]),
        ]}
        onChange={(newValue: DateRange<unknown>) => {
          setActivityDate(newValue as Date[]);
        }}
      />
    </LocalizationProvider>
  );
};

export default DataDateRangePicker;
